import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { RootState } from '../index';

// Define the WorkflowEvent interface
export interface WorkflowEvent {
  value: string;
  label: string;
  description: string;
  db_operation: string | null;
  validation_rules: {
    required_fields?: boolean;
    check_duplicates?: boolean;
    validate_references?: boolean;
    check_existence?: boolean;
    check_dependencies?: boolean;
    [key: string]: boolean | number | string | undefined;
  };
  handler?: string;
  fields?: string[];
  model?: string;
}

// Response is a direct array of events, not paginated
export type WorkflowEventResponse = WorkflowEvent[];

// Create API
export const workflowEventApi = createApi({
  reducerPath: 'workflowEventApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['WorkflowEvent'],
  endpoints: (builder) => ({
    // Get all workflow events
    getWorkflowEvents: builder.query<WorkflowEventResponse, { dropdown?: boolean; entity_type?: string } | void>({
      query: (params) => ({
        url: 'events/',
        params: {
          ...(params?.dropdown !== undefined ? { dropdown: params.dropdown } : {}),
          ...(params?.entity_type ? { entity_type: params.entity_type } : {}),
        }
      }),
      providesTags: ['WorkflowEvent'],
    }),
    
    // Get workflow event by value
    getWorkflowEventByValue: builder.query<WorkflowEvent, string>({
      query: (value) => `events/${value}/`,
      providesTags: (result, error, value) => [{ type: 'WorkflowEvent', id: value }],
    }),
    
    // Get workflow event by ID
    getWorkflowEventById: builder.query<WorkflowEvent, string>({
      query: (id) => `events/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowEvent', id }],
    }),
    
    // Check for duplicate workflows based on entity type, role, and event
    checkDuplicateWorkflow: builder.query<
      { isDuplicate: boolean; duplicateWorkflows: string[] },
      { entity_type: string; event_type: string; role?: string }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.entity_type) queryParams.append('entity_type', params.entity_type);
        if (params.event_type) queryParams.append('event_type', params.event_type);
        if (params.role) queryParams.append('role', params.role);
        
        return {
          url: 'workflow-templates/check-duplicate/',
          params: queryParams
        };
      }
    })
  }),
});

// Export hooks
export const {
  useGetWorkflowEventsQuery,
  useGetWorkflowEventByValueQuery,
  useGetWorkflowEventByIdQuery,
  useCheckDuplicateWorkflowQuery,
} = workflowEventApi;